import { initializeApp, type FirebaseApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, type Firestore } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator, type Functions } from "firebase/functions";
import { getStorage, connectStorageEmulator, type FirebaseStorage } from "firebase/storage";
import { requireEnv, requireEnvBoolean } from "@/lib/requireEnv";

/**
 * Firebase singletons.
 *
 * Everything is read through `requireEnv`, so a missing variable throws at
 * startup with the variable named rather than producing an app that loads and
 * then fails on the first call.
 */

let app: FirebaseApp | undefined;

function ensureApp(): FirebaseApp {
  if (app) return app;

  const projectId = requireEnv("VITE_FIREBASE_PROJECT_ID");
  app = initializeApp({
    apiKey: requireEnv("VITE_FIREBASE_API_KEY"),
    authDomain: requireEnv("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId,
    appId: requireEnv("VITE_FIREBASE_APP_ID"),
    /*
      The project's default bucket, which is named after the project. Derived
      rather than configured: it is not a choice anybody makes, and the
      functions' `STORAGE_BUCKET` and every e2e script name it the same way.
      Only the pictures on beats are read from it (review timeline).
    */
    storageBucket: `${projectId}.firebasestorage.app`,
  });

  /*
   * App Check, when this deployment is meant to have it.
   *
   * Explicitly switched rather than inferred from whether a key happens to be
   * set: a back office that quietly stopped attesting itself would look exactly
   * like one that never did, and "is App Check on in production?" must have an
   * answer you can read off the configuration. `VITE_APP_CHECK=false` is a
   * decision; a missing `VITE_APP_CHECK` is a mistake, and throws.
   *
   * Google's own alert is what prompted this: reCAPTCHA Enterprise keys were
   * created for this app and for the admin console and neither app had ever
   * called App Check, so the keys protected nothing (2026-09-14). It goes here,
   * immediately after `initializeApp` and before any service is fetched,
   * because a token must exist before the first call that carries one.
   */
  if (requireEnvBoolean("VITE_APP_CHECK")) {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(requireEnv("VITE_RECAPTCHA_SITE_KEY")),
      isTokenAutoRefreshEnabled: true,
    });
  }

  if (requireEnvBoolean("VITE_USE_EMULATOR")) {
    // Required, not assumed. The browser that runs the end-to-end tests lives
    // in a container, where 127.0.0.1 is the container itself and the emulator
    // is on the host. Naming the host makes the two environments identical
    // instead of quietly different.
    const host = requireEnv("VITE_EMULATOR_HOST");
    connectAuthEmulator(getAuth(app), `http://${host}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(getFirestore(app), host, 8080);
    connectFunctionsEmulator(getFunctions(app, requireEnv("VITE_FUNCTIONS_REGION")), host, 5001);
    connectStorageEmulator(getStorage(app), host, 9199);

    // Loud on purpose. A developer who does not notice they are on the
    // emulator will eventually be confused about why their data vanished.
    // eslint-disable-next-line no-console
    console.info("[spontar] connected to the local emulator suite");
  }

  return app;
}

export const firebaseApp = (): FirebaseApp => ensureApp();
export const auth = (): Auth => getAuth(ensureApp());
export const db = (): Firestore => getFirestore(ensureApp());
export const storage = (): FirebaseStorage => getStorage(ensureApp());
export const functions = (): Functions =>
  getFunctions(ensureApp(), requireEnv("VITE_FUNCTIONS_REGION"));

/**
 * A picture link from the backend, made reachable from this browser.
 *
 * Production links are Firebase's own download links and pass through as
 * they are. Under the emulator, the functions emulator builds them with the
 * address it sees the storage emulator on, which is not the one a browser in
 * the end-to-end container can reach — so the origin becomes the emulator
 * host this app was told about, as every other emulator connection does.
 */
export function reachableStorageUrl(url: string): string {
  if (!requireEnvBoolean("VITE_USE_EMULATOR")) return url;
  return url.replace(/^https?:\/\/[^/]+/, `http://${requireEnv("VITE_EMULATOR_HOST")}:9199`);
}
